import axios from 'axios';

import { handleResponseError, initialState } from '@/Helpers.js';
import Model from '@/models/Model.js';

export default class BellNotification extends Model {
    static entity = 'bell_notifications';

    static modelName = 'BellNotification';

    static primaryKey = 'notification_id';

    static state() {
        return {
            counter: 0,
            page: 1,
            last_page: 1,
        };
    }

    static fields() {
        return {
            id: this.attr(null),
            notification_id: this.attr(null),
            course_id: this.attr(null),
            activity_id: this.attr(null),
            title: this.attr(null),
            body: this.attr(null),
            creator: this.attr(null),
            type: this.attr(null),
            read: this.boolean(null),
            course_code: this.attr(null),
            class: this.attr(null),
            created_at: this.attr(null),

            ...super.fields(),
        };
    }

    static async fetch(page = 1) {
        try {
            const { response } = await this.api().get(`/api/notifications?page=${page}`);
            await this.commit(state => {
                // Counter mirrors the aggregated inbox tiles (unread_count); fall back to
                // the paginated total for older payloads.
                state.counter = response.data.meta?.unread_count ?? response.data.meta?.total ?? 0;
                state.last_page = response.data.meta?.last_page;
            });
            return response;
        } catch (e) {
            handleResponseError(e);
        }

        return false;
    }

    async markAsRead() {
        this.$update({ read: true });
        await axios.put('/api/mark-as-read', {
            readable_id: this.id,
            readable_type: this.class,
        });
    }

    static async markMultipleAsRead(type, ids) {
        await axios.put('/api/mark-as-read', {
            readable_type: type,
            readable_ids: ids,
        });
    }

    async markAsUnread() {
        await axios.put('/api/mark-as-unread', {
            readable_id: this.id,
            readable_type: this.class,
        });
    }

    get drawer_styles() {
        let styles;

        if (this.type === 'Activity Update') {
            styles = {
                label: 'Activity Update',
                style: 'background-color:#335CE5',
                icon: 'unicons uil uil-file-plus is-size-8',
                actionButtonLabel: 'View Activity',
            };
        }

        if (this.type === 'Course Announcement') {
            styles = {
                label: 'Course Announcement',
                style: 'background-color:#ECA807',
                icon: 'unicons uil uil-megaphone is-size-8',
                actionButtonLabel: 'View Course',
            };
        }

        if (this.type === 'Message') {
            styles = {
                label: 'Message',
                style: 'background-color:#7160A6',
                icon: 'unicons uil uil-envelope is-size-8',
                actionButtonLabel: 'View Message',
            };
        }

        if (this.type === 'Discussion Reply') {
            styles = {
                label: 'Discussion Reply',
                style: 'background-color:#0EA5E9',
                icon: 'unicons uil uil-comment-dots is-size-8',
                actionButtonLabel: 'View Discussions',
            };
        }

        return styles;
    }

    get inbox_link() {
        if (this.type === 'Activity Update') {
            return `/inbox/announcements/course/${this.course_id}/activity-update/${this.id}`;
        }

        if (this.type === 'Course Announcement') {
            return `/inbox/announcements/course/${this.course_id}/course-announcement/${this.id}`;
        }

        if (this.type === 'Message') {
            return `/inbox/messages/${this.id}`;
        }

        if (this.type === 'Discussion Reply') {
            return '/inbox/discussions';
        }

        return null;
    }

    get current_page() {
        return initialState('bell_notification.page');
    }

    get formatted_created_at() {
        if (!this.created_at) {
            return 'N/A';
        }

        return this.timestamp('created_at').format('ddd M/D [at] h:mm A');
    }
}
